import {
  band,
  breakpoints,
  Col,
  frosted,
  Icon,
  type InputHandle,
  Item,
  onWeb,
  ScrollArea,
  Search,
  Tappable,
  Text,
  Tooltip,
  useTheme,
  type ViewStyle,
} from "@rally/ui-kit";
import { useCallback, useEffect, useRef, useState } from "react";
import { say } from "../localization/say.ts";
import { WhenLabel } from "../localization/when-label.tsx";
import { read } from "../net/rally.ts";
import { goTo, useWhere } from "../router.ts";
import { Swipe } from "../swipe.tsx";
import { whenSearchIsAsked } from "./ask.ts";
import { Filters } from "./filters.tsx";
import { slashOpensSearch } from "./focus.ts";
import {
  type Filter,
  type Found,
  type Row as FoundRow,
  type Group,
  groupsOf,
  onEscape,
  stepTo,
  walkable,
} from "./found.ts";
import { suggested, type WhereIAm } from "./suggest.ts";

const _FIELD = 44;
const _BELOW = 8;

const A_PAUSE = 140;

const A_LINE_IS = 120;

const PANEL_IS_TALL = 460;

export function Find() {
  const theme = useTheme();
  const narrow = theme.isAtMost(breakpoints.phone);
  const [typed, setTyped] = useState("");

  const [open, setOpen] = useState(false);
  const [found, setFound] = useState<Found | null>(null);
  const [filter, setFilter] = useState<Filter>("all");

  const [tree, setTree] = useState<WhereIAm | null>(null);

  const standingIn = useWhere().conversation ?? null;
  const [broke, setBroke] = useState(false);
  const [at, setAt] = useState(-1);
  const box = useRef<InputHandle | null>(null);

  const fragment = typed.trim();

  useEffect(() => {
    if (fragment === "") {
      setFound(null);
      setBroke(false);
      return;
    }
    let live = true;
    const timer = setTimeout(() => {
      read<Found>("search", {
        q: fragment,
        ...(standingIn ? { current: standingIn } : {}),
      })
        .then((answer) => {
          if (!live) return;
          setFound(answer);
          setBroke(false);
        })
        .catch(() => {
          if (live) setBroke(true);
        });
    }, A_PAUSE);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [fragment, standingIn]);

  const settled = found !== null && found.fragment === fragment;

  const groups: Group[] =
    fragment === ""
      ? filter === "all" || filter === "conversations"
        ? suggested(tree, standingIn)
        : []
      : settled
        ? groupsOf(found, filter)
        : [];
  const rows = walkable(groups);
  const here = rows[at] ?? null;

  useEffect(() => {
    if (!open || tree !== null) return;
    let live = true;
    read<WhereIAm>("where.i.am")
      .then((answer) => {
        if (live) setTree(answer);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [open, tree]);

  const shut = useCallback(() => {
    setOpen(false);
    setAt(-1);
    (box.current as unknown as { blur?: () => void } | null)?.blur?.();
    if (onWeb)
      requestAnimationFrame(() => {
        if (!document.querySelector('[data-testid="find-field"]'))
          document.querySelector<HTMLElement>('[data-testid="find-open"]')?.focus();
      });
  }, []);

  const openRow = (row: FoundRow) => {
    if (!row.goes) return;
    setTyped("");
    shut();
    goTo(row.goes);
  };

  useEffect(() => {
    if (!onWeb || typeof window === "undefined") return;
    const onKey = (event: KeyboardEvent) => {
      const palette = (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k";
      if (!palette) {
        if (event.key !== say("find.shortcut")) return;
        if (!slashOpensSearch(document.activeElement)) return;
      }
      event.preventDefault();
      setOpen(true);

      setTimeout(() => (box.current as unknown as { focus?: () => void } | null)?.focus?.(), 0);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(
    () =>
      whenSearchIsAsked((fragment) => {
        setTyped(fragment);
        setAt(-1);
        setOpen(true);
        setTimeout(() => (box.current as unknown as { focus?: () => void } | null)?.focus?.(), 0);
      }),
    [],
  );

  const key = (event: KeyboardEvent) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      setOpen(true);
      setAt((was) => stepTo(rows.length, was, event.key === "ArrowDown" ? 1 : -1));
      return;
    }
    if (event.key === "Enter") {
      if (here) {
        event.preventDefault();
        openRow(here);
      }
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();

      const next = onEscape({ typed, open });
      setTyped(next.typed);
      setAt(-1);
      if (!next.open) shut();
    }
  };

  const field = (
    <Search
      value={typed}
      onChangeText={(text) => {
        setTyped(text);
        setAt(-1);
        setOpen(true);
        setTimeout(() => (box.current as unknown as { focus?: () => void } | null)?.focus?.(), 0);
      }}
      onClear={() => {
        setTyped("");
        setAt(-1);
        (box.current as unknown as { focus?: () => void } | null)?.focus?.();
      }}
      placeholder={say("find.placeholder")}
      inputRef={box}
      testID="find-field"
      onKeyPress={key}
    />
  );

  const panel = (
    <Col
      testID="find-panel"
      style={{
        flex: narrow ? 1 : undefined,
        overflow: "hidden",
      }}
    >
      <ScrollArea
        style={narrow ? undefined : { maxHeight: PANEL_IS_TALL }}
        keyboardShouldPersistTaps="handled"
      >
        <Col style={{ paddingVertical: theme.space[2] }}>
          <Filters
            selected={filter}
            onSelect={(next) => {
              setFilter(next);
              setAt(-1);
              setTimeout(() => (box.current as unknown as { focus?: () => void } | null)?.focus?.(), 0);
            }}
          />
          {fragment === "" && groups.length === 0 ? (
            <Say testID="find-reaches">{say("find.reaches")}</Say>
          ) : broke ? (
            <Say testID="find-broke" tone="danger">
              {say("find.broke")}
            </Say>
          ) : settled && groups.length === 0 ? (
            <Say testID="find-nothing">{say("find.nothing")(fragment)}</Say>
          ) : (
            groups.map((group) => (
              <Col key={group.id} testID={`find-group-${group.id}`} style={{ paddingBottom: theme.space[2] }}>
                <Text
                  typeName="caption"
                  color="text.tertiary"
                  style={{
                    paddingHorizontal: theme.space[4],
                    paddingTop: theme.space[2],
                    paddingBottom: theme.space[1],
                  }}
                >
                  {group.label}
                </Text>
                {group.rows.map((row) => (
                  <Line
                    key={row.key}
                    row={row}
                    selected={here?.key === row.key}
                    onOpen={() => openRow(row)}
                  />
                ))}
              </Col>
            ))
          )}
        </Col>
      </ScrollArea>
    </Col>
  );

  const trigger = (
    <Tooltip
      below
      content={`${say("find.open")} (${say("find.shortcut")})`}
      accessibilityLabel={say("find.open")}
      onPress={() => {
        setOpen(true);
        setTimeout(() => box.current?.focus(), 0);
      }}
      testID="find-open"
    >
      <Col style={{ width: 44, height: 44, alignItems: "center", justifyContent: "center" }}>
        <Icon name="search" size={20} color="text.secondary" />
      </Col>
    </Tooltip>
  );

  if (narrow) {
    if (!open) return trigger;
    return (
      <Swipe testID="find" zIndex={26} onDismiss={shut}>
        <Col
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: theme.space[2],
            height: band.heightNarrow,
            paddingHorizontal: theme.space[3],
            borderBottomWidth: 1,
            borderBottomColor: theme.color["border.default"],
          }}
        >
          <Tappable
            onPress={() => {
              setTyped("");
              shut();
            }}
            accessibilityRole="button"
            accessibilityLabel={say("find.close")}
            testID="find-back"
            style={{ paddingVertical: theme.space[2] }}
          >
            <Icon name="arrow-left" size={20} color="text.secondary" />
          </Tappable>
          <Col style={{ flex: 1 }}>{field}</Col>
        </Col>
        <Col style={{ flex: 1, padding: theme.space[3] }}>{panel}</Col>
      </Swipe>
    );
  }

  return (
    <Col testID="find">
      {trigger}
      {open ? (
        <Tappable
          style={
            {
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(4,7,12,0.58)",
            } as unknown as ViewStyle
          }
          onPress={shut}
          accessibilityRole="button"
          accessibilityLabel={say("find.close")}
          testID="find-away"
        />
      ) : null}
      {open ? (
        <Col
          style={
            {
              position: "fixed",
              top: "12vh",
              left: 0,
              right: 0,
              alignItems: "center",
              zIndex: 31,
            } as unknown as ViewStyle
          }
        >
          <Col
            testID="find-surface"
            style={
              {
                width: "calc(100% - 48px)",
                maxWidth: 760,
                borderRadius: theme.radius.lg,
                borderWidth: 1,
                borderColor: theme.color["border.default"],
                overflow: "hidden",
                ...theme.elevation.raised,
                ...(frosted(theme, 30) as object),
              } as unknown as ViewStyle
            }
          >
            <Col
              style={{
                padding: theme.space[3],
                borderBottomWidth: 1,
                borderBottomColor: theme.color["border.default"],
              }}
            >
              {field}
            </Col>
            {panel}
          </Col>
        </Col>
      ) : null}
    </Col>
  );
}

function Say({
  children,
  tone = "text.secondary",
  testID,
}: {
  children: string;
  tone?: "text.secondary" | "danger";
  testID: string;
}) {
  const theme = useTheme();
  return (
    <Text
      testID={testID}
      typeName="body"
      color={tone}
      style={{ paddingHorizontal: theme.space[4], paddingVertical: theme.space[3] }}
    >
      {children}
    </Text>
  );
}

function Line({ row, selected, onOpen }: { row: FoundRow; selected: boolean; onOpen: () => void }) {
  const title = row.title.length > A_LINE_IS ? `${row.title.slice(0, A_LINE_IS)}…` : row.title;
  return (
    <Item
      title={title}
      {...(row.subtitle ? { subtitle: row.subtitle } : {})}
      icon={row.icon}
      right={row.at ? <WhenLabel at={row.at} numberOfLines={1} /> : undefined}
      selected={selected}
      onPress={onOpen}
      testID={`find-row-${row.kind}`}
    />
  );
}
